import Checklist from 'images/illustrations/Checklist.svg';
import RocketLaunch from 'images/illustrations/RocketLaunch.svg';
import Security from 'images/illustrations/Security.svg';
import { SiteRole } from 'types';
import { IAnnouncement } from './types';

export const announcements: IAnnouncement[] = [
  {
    id: '2026-03-unintegrated-release',
    bannerTitle: 'We have new features! Click here to check them out.',
    title: 'New features',
    description: 'New features and improvements.',
    heroImage: {
      src: RocketLaunch,
      alt: 'Rocket launching',
      maxWidth: 260,
    },
    body: {
      items: [
        {
          title: '#1 - Payroll Withholdings',
          description:
            'Now available in the Manual and Spreadsheet Upload payment flows.\n' +
            'Withhold a portion of employees’ tips to be paid through payroll—making it easier to cover benefits, deductions, and garnishments, prevent negative paychecks, and keep wage reporting clean and accurate.',
          ctaLabel: 'Watch the walkthrough',
          ctaHref: 'https://kickfin.navattic.com/pwi027b',
        },
        {
          title: '#2 - Cash Tip Tracking',
          description: 'Also available in the Manual and Spreadsheet Upload payment flows.\nRecord cash tips paid directly to employees to help ensure compliance with tipped wage reporting requirements.',
          ctaLabel: 'Watch the walkthrough',
          ctaHref: 'https://kickfin.navattic.com/uh4h0opw',
        },
        {
          title: '#3 - Improvements to the Spreadsheet Upload Payment Flow',
          description: 'For customers using the upload flow, we’ve added improvements to make employee notes and templates easier to manage.',
          bullets: ['The ability to include individual notes per employee', 'Downloadable, ready-to-use templates in all three accepted formats'],
        },
        {
          title: '#4 - Additional Improvements',
          description: 'We’ve made additional improvements, including:',
          bullets: ['Improved mobile experience when issuing tip payouts', 'Business day selection now available at the start of the payment process'],
        },
      ],
    },
    roles: [SiteRole.ADMIN, SiteRole.MANAGER],
    expiresAt: '2026-03-12T00:00:00.000Z',
  },
  {
    id: '2026-04-rel-2-4-2',
    bannerTitle: 'We have new features! Click here to check them out.',
    title: 'New features',
    description: 'New features and improvements.',
    heroImage: {
      src: RocketLaunch,
      alt: 'Rocket launching',
      maxWidth: 260,
    },
    body: {
      items: [
        {
          title: '#1 - Available for All Customers: Pay Up to 150 Employees at a Time',
          description: 'Large sites can now issue payments for up to 150 employees at once. No more need to divide your employees into groups when making payments.',
        },
        {
          title: '#2 - Tip Calculator Customers: New Configurable Grace Period',
          description:
            'Customers using the tip calculator can now customize a Grace Period to smooth shift transitions. Employees clocking out slightly late after the end of a day segment stay in their earlier segment.\n' +
            'Employees clocking in slightly early ahead of a day are moved forward to their segment. If you’ve configured separate AM and PM job codes in the past just ensure that AM and PM employees never\n' +
            'have overlapping tip pools then this feature is for you. No more separate AM and PM job codes needed. The grace period will handle this overlap for you.\n' +
            'Contact Kickfin Support for assistance with this feature.',
        },
        {
          title: '#3 - Employee Dashboard Improvements',
          description:
            'Pending and expired payments are now more prominently displayed in the Employee Dashboard, making it easier to nudge employees toward resolution.\n' +
            'When employees log in, they’ll see a dollar amount and count for any outstanding payments, with a direct link to view the full list.\n' +
            'These issues are caused by debit card problems, so employees should update their card or contact their bank to see if they are blocking payments.\n' +
            'For additional help, have them reach out to Kickfin Support.',
        },
      ],
    },
    roles: [SiteRole.ADMIN, SiteRole.MANAGER],
    expiresAt: '2026-04-12T00:00:00.000Z',
  },
  {
    id: '2026-04-security-practices',
    bannerTitle: 'Important! Kickfin Security Practices',
    title: 'Kickfin Security Practices',
    description: 'Please review these account and support safety reminders.',
    heroImage: {
      src: Security,
      alt: 'Security shield',
      maxWidth: 260,
    },
    body: {
      items: [
        {
          title: 'Best Practices for Your Account',
          description: '',
          bullets: [
            'Never share your password with anyone else',
            'Never share the security code sent to your phone with anyone else',
            'Do not use shared accounts - every Kickfin user should have their own account with their own password and phone number',
            'If you use a shared terminal at your restaurant, be sure to logout after you’re done connecting to Kickfin',
            'When an employee leaves, remember to dismiss them from your site',
            'Employees should never share their full debit card number or CVV with anyone else',
            'Kickfin supports a variety of multi-manager-approval features. Contact Kickfin Support to have these enabled at your locations',
          ],
        },
        {
          title: 'How to Recognize Legitimate Kickfin Support',
          description: '',
          bullets: [
            'Kickfin support will NEVER ask for your password',
            'Kickfin support will NEVER ask for the 6 digit passcode sent to your phone',
            'Kickfin support will NEVER ask for your full debit card number',
            'Kickfin support will NEVER ask for a photo of your debit card',
            'Kickfin support MAY request the first 6 digits of your card number or the last 4 digits of your card when assisting with payment issues',
            'Kickfin support will only text you from 737-258-9666 or call you from 737-252-6785',
          ],
        },
      ],
    },
    roles: [SiteRole.ADMIN, SiteRole.MANAGER],
    expiresAt: '2026-04-28T00:00:00.000Z',
  },
  {
    id: '2026-04-rel-2-4-4',
    bannerTitle: 'Recent Updates: Feature changes for integrated and unintegrated customers',
    title: 'New features',
    description: 'New features and improvements.',
    heroImage: {
      src: RocketLaunch,
      alt: 'Rocket launching',
      maxWidth: 260,
    },
    body: {
      items: [
        {
          title: '#1 - Unintegrated Customers - New limitation on backdating payments',
          description:
            'To help keep your payroll records accurate and secure, Kickfin now limits how far back you can select a business date when issuing manual or upload payments.\n' +
            'By default, you can select a business date up to 7 days in the past — so if you need a few days extra to issue payments for your site there’s still a reasonable window to do so.\n' +
            'If you try to select a date outside that range, you’ll see a clear message letting you know the business date is closed for payments.\n' +
            ' This setting can be adjusted by your account administrator if your business needs a different window.',
        },
        {
          title: '#2 - Tip Calculator Users - All sites now use our Advanced Rules setting',
          description:
            'We’ve upgraded all locations to our advanced tip calculator rules, unlocking more powerful and flexible options for distributing tips.\n' +
            'If you visit your tip calculator settings, you may notice some additional configuration options that weren’t there before.\n' +
            'These new options give you greater control over how tips are distributed — but no changes have been made to your existing setup, so everything will continue to work just as it did.\n' +
            'If you have any questions about your settings or would like help exploring the new options, please reach out to our support team.',
        },
      ],
    },
    roles: [SiteRole.ADMIN, SiteRole.MANAGER],
    expiresAt: '2026-05-12T00:00:00.000Z',
  },
  {
    // Pre-maintenance heads-up. Visible from June 11, 9am ET (13:00 UTC) until the maintenance window opens (June 16, 6am ET = 10:00 UTC).
    id: '2026-06-pre-maintenance',
    bannerTitle: 'Heads up: Planned maintenance on Tuesday, June 16th',
    title: 'Planned Maintenance',
    description: 'Kickfin has scheduled platform maintenance for Tuesday, June 16th.',
    heroImage: {
      src: Checklist,
      alt: 'Checklist',
      maxWidth: 260,
    },
    body: {
      items: [
        {
          title: 'Planned Maintenance: June 16th, 6am–9am Eastern Time',
          description:
            'Kickfin will be performing scheduled platform maintenance on Tuesday, June 16th from 6:00am to 9:00am Eastern Time.\n' +
            'During this window some functionality may be temporarily unavailable. We recommend issuing any time-sensitive payments before maintenance begins.\n' +
            'No action is required on your part — service will resume automatically once maintenance is complete.',
        },
      ],
    },
    roles: [SiteRole.ADMIN, SiteRole.MANAGER],
    startsAt: '2026-06-11T13:00:00.000Z',
    expiresAt: '2026-06-16T10:00:00.000Z',
  },
  {
    // Post-maintenance announcement. Visible once the maintenance window closes (June 16, 9am ET = 13:00 UTC) until ~June 21.
    id: '2026-06-owner-rules',
    bannerTitle: 'We have new features! Click here to check them out.',
    title: 'New features',
    description: 'New features and improvements.',
    heroImage: {
      src: RocketLaunch,
      alt: 'Rocket launching',
      maxWidth: 260,
    },
    body: {
      items: [
        {
          title: 'Introducing Owner Rules',
          description:
            'Tip share rules can now be set up by check owner — not just by job.\n' +
            'That means online ordering systems, shared bar terminals, and other virtual POS users no longer need daily clock-ins or special job codes. Just create an Owner Rule, and Kickfin handles the rest.',
        },
        {
          description:
            'For real employees Job Rules are still the right choice in most instances, but Owner Rules give you a clean way to handle special cases —\n' +
            'like managers who don’t accept tips, owners who occasionally cover tables, or staff who opt out of pooling.',
        },
        {
          description: 'You’ll also notice "Shared POS" is now called "Virtual" throughout the portal to better reflect how it’s used today.',
        },
      ],
    },
    roles: [SiteRole.ADMIN, SiteRole.MANAGER],
    startsAt: '2026-06-16T13:00:00.000Z',
    expiresAt: '2026-06-21T00:00:00.000Z',
  },
  {
    id: '2026-08-tip-calculator-start-business-day',
    bannerTitle: 'New Calculator Features! Click here to check them out.',
    title: 'New Calculator Features',
    description: '',
    heroImage: {
      src: RocketLaunch,
      alt: 'Rocket launching',
      maxWidth: 260,
    },
    body: {
      items: [
        {
          title: 'New Start of Business Day Setting',
          description:
            'If your restaurant operates 24 hours a day, you can now specify a Start of Business Day in the Day Segments configuration for your site.\n' +
            'All Day Segments will fall within this window, and the calculator will correctly handle segments that start after midnight and employee clock-ins that occur after midnight.\n' +
            'Make sure the Start of Business Day that you select matches the setup on your POS.',
        },
      ],
    },
    roles: [SiteRole.ADMIN, SiteRole.MANAGER],
    startsAt: '2026-08-18T13:00:00.000Z',
    expiresAt: '2026-08-23T00:00:00.000Z',
  },
  {
    id: '2026-09-rel-2.4.8-original-tip-calculator-rules',
    bannerTitle: 'New Calculator Features! Click here to check them out.',
    title: 'New Calculator Features',
    description: '',
    heroImage: {
      src: RocketLaunch,
      alt: 'Rocket launching',
      maxWidth: 260,
    },
    body: {
      items: [
        {
          title: 'Recalculate with original or current settings',
          description:
            'Tip calculator customers now have another tool to evaluate changes in calculations. When you rerun the calculator on an already-paid payment set, you can now\n' +
            'choose whether to use the calculator settings from when the payment was originally calculated or the site’s current settings - and see exactly how the amounts changed,\n' +
            'with the option to export a summary of the differences.',
        },
        {
          description: 'This choice is available for payment sets issued after August 18th; older payment sets will automatically use current settings. Look for the new prompt the next time you recalculate a payment set.',
        },
      ],
    },
    roles: [SiteRole.ADMIN, SiteRole.MANAGER],
    startsAt: '2026-09-15T13:00:00.000Z',
    expiresAt: '2026-09-21T00:00:00.000Z',
  },
  {
    id: '2026-11-close-pay-periods',
    bannerTitle: 'New Close of Pay Period Feature! Click here to check it out.',
    title: 'New Feature - Close Pay Periods',
    description: '',
    heroImage: {
      src: RocketLaunch,
      alt: 'Rocket launching',
      maxWidth: 260,
    },
    body: {
      items: [
        {
          description: "Keep payroll reports accurate by closing a pay period once it's processed. You can review each day's payments before you close. After it's closed:",
          bullets: ['No new payments can be added for those dates.', "Pending payments can't be canceled or switched to payroll.", 'Org Admins can reopen it if something needs fixing.'],
        },
        {
          title: 'Optional: convert undelivered payments to payroll',
          description: 'Have any undelivered instant payments automatically converted to payroll when you close, so employees still get paid all of their tips by payday. Contact Kickfin Support to turn this on.',
        },
        {
          description: 'Get started on the Pay Period page.',
        },
      ],
    },
    roles: [SiteRole.ADMIN, SiteRole.MANAGER],
    startsAt: '2026-11-03T13:00:00.000Z',
    expiresAt: '2026-11-09T00:00:00.000Z',
  },
];
